import { useState } from "react";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { SignedAvatarImage } from "@/components/signed-avatar-image";
import { RowCard } from "@/components/list/row-card";
import { RowIndex } from "@/components/list/row-index";
import { Section } from "@/components/section";
import { Chip } from "@/components/badge/chip";
import { ListEmpty, ListLoading } from "@/components/list/status-text";
import { RefreshButton } from "@/components/list/refresh-button";
import UserSelector from "@/components/user-selector";
import type { PickerUser } from "@/components/user-selector";
import { fetcher, groupUsersByRole, postFetcher, roleLabel } from "@/lib/swr";
import type { User } from "@/lib/swr";

// Handover: transfer all open PRDs owned by this user to a picked user.
// Lives on the user detail page; the "from" side is implied by the page.
export const TransferPrdsButton = ({ user }: { user: User }) => {
  const [result, setResult] = useState<string | null>(null);
  const { trigger, isMutating } = useSWRMutation(
    "/prds/transfer-owner",
    postFetcher,
    {
      onSuccess: (data: { transferred: number }) =>
        setResult(`${data.transferred} transferred`),
      onError: () => setResult("failed"),
    }
  );
  return (
    <div className="flex items-center gap-1.5">
      {result && (
        <span className="text-xs text-muted-foreground">{result}</span>
      )}
      <UserSelector
        hideUsers={[user as PickerUser]}
        onContinue={(to) => {
          if (!to) return;
          setResult(null);
          trigger({ fromUserId: String(user.id), toUserId: String(to.id) });
        }}
        buttonText={isMutating ? "Transferring…" : "Transfer PRDs"}
        title="Transfer PRDs"
        description={`Hand over all open PRDs owned by ${user.name ?? "this user"} to the selected user.`}
      />
    </div>
  );
};

// Slim list row: identity + counts only. Click through to the user detail
// page for the full picture (roles, visible apps, transfer, delete) —
// inline chips became unreadable once users had many visible apps.
const UserCard = ({ user, index }: { user: User; index: number }) => {
  const navigate = useNavigate();
  const roles: string[] = user.roles || [];
  const appCount = (user.visibleApps || []).filter((app) => !app.archived).length;

  return (
    <RowCard onClick={() => navigate(`/admin/users/${user.id}`)}>
      <RowIndex n={index + 1} />

      <div className="flex min-w-0 flex-1 items-center gap-2">
        <Avatar className="shrink-0">
          <SignedAvatarImage src={user.avatarThumb} alt="Image" />
          <AvatarFallback>{user.name?.[0]}</AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <p className="truncate text-sm font-medium leading-none">
            {user.name}
          </p>
          <p className="truncate text-sm text-muted-foreground">{user.email}</p>
        </div>
      </div>

      {/* Role chips (usually 1–2); admin shown separately in red */}
      <div className="flex shrink-0 flex-wrap items-center justify-end gap-1">
        {user.isAdmin && (
          <Chip tone="red" className="flex h-6 items-center px-2">
            admin
          </Chip>
        )}
        {user.kind === "bot" && (
          <Chip className="flex h-6 items-center px-2">bot</Chip>
        )}
        {roles.map((role, idx) => (
          <Chip key={idx} tone="green" className="flex h-6 items-center px-2">
            {roleLabel(role)}
          </Chip>
        ))}
      </div>

      {/* Visible apps as a count — full list lives on the detail page */}
      <span className="w-20 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
        {appCount} apps
      </span>
    </RowCard>
  );
};

const UserRoles = () => {
  const { data: users, isLoading, isValidating, mutate } = useSWR<User[]>(
    ["/users/full"],
    fetcher
  );

  const list: User[] = users || [];

  return (
    <Section
      title="User Roles"
      action={<RefreshButton spinning={isValidating} onClick={() => mutate()} />}
    >
      <div className="flex flex-col gap-5">
        {isLoading && <ListLoading />}
        {!isLoading && list.length === 0 && <ListEmpty>No users.</ListEmpty>}
        {/* 按角色分组(与 App 设置页 Members 同口径):一人多角色在每组各出现一次 */}
        {groupUsersByRole(list).map((g) => (
          <div key={g.key} className="space-y-1">
            <div className="flex items-center gap-1.5 text-sm font-medium">
              {g.label}
              <span className="text-xs font-normal text-muted-foreground">
                {g.users.length}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              {g.users.map((user, index) => (
                <UserCard key={user.id} user={user} index={index} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
};

export default UserRoles;
